import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { usePagination } from '../hooks/usePagination';
import Pagination from './Pagination';

interface Callback {
  id: number;
  transactionIndex: string;
  signature: string;
  status: string;
  payload: any;
  sourceIp: string;
  createdAt: string;
}

interface CallbackStats {
  total: number;
  successful: number;
  failed: number;
  pending: number;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

const CallbackLogs: React.FC = () => {
  const [callbacks, setCallbacks] = useState<Callback[]>([]);
  const [stats, setStats] = useState<CallbackStats>({
    total: 0,
    successful: 0,
    failed: 0,
    pending: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedCallback, setSelectedCallback] = useState<Callback | null>(null);
  const [excludeLocalhost, setExcludeLocalhost] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const { page, pageSize, setPage, setPageSize } = usePagination();

  const fetchCallbacks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/callback/list?page=${page}&limit=${pageSize}&excludeLocalhost=${excludeLocalhost}`,
      );
      setCallbacks(response.data.data);
      setTotalItems(response.data.meta.total);
      setTotalPages(response.data.meta.totalPages);
    } catch (error) {
      console.error('Error fetching callbacks:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, excludeLocalhost]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/callback/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchCallbacks();
    fetchStats();
    const interval = setInterval(() => {
      fetchCallbacks();
      fetchStats();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchCallbacks, fetchStats]);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'bg-green-500/15 text-green-300 ring-1 ring-inset ring-green-500/30';
      case 'failed':
      case 'error':
        return 'bg-red-500/15 text-red-300 ring-1 ring-inset ring-red-500/30';
      case 'pending':
        return 'bg-yellow-500/15 text-yellow-300 ring-1 ring-inset ring-yellow-500/30';
      default:
        return 'bg-slate-700 text-slate-300 ring-1 ring-inset ring-slate-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-400" />;
      case 'failed':
      case 'error':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />;
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-400" />;
      default:
        return <ClipboardDocumentCheckIcon className="h-5 w-5 text-slate-400" />;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

  const stats_cards = [
    { name: 'Total Callbacks', value: stats.total, icon: ClipboardDocumentCheckIcon, color: 'bg-blue-500' },
    { name: 'Successful', value: stats.successful, icon: CheckCircleIcon, color: 'bg-green-500' },
    { name: 'Failed', value: stats.failed, icon: ExclamationTriangleIcon, color: 'bg-red-500' },
    { name: 'Pending', value: stats.pending, icon: ClockIcon, color: 'bg-yellow-500' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-100">Callback Logs</h1>
        <p className="mt-2 text-sm text-slate-400">
          View all transaction callbacks received by the system
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats_cards.map((item) => (
          <div
            key={item.name}
            className="rounded-xl border border-slate-700 bg-slate-800 shadow-sm transition-colors hover:border-slate-600"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className={`flex-shrink-0 rounded-lg p-2 ${item.color} bg-opacity-20`}>
                  <item.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-400 truncate">{item.name}</dt>
                    <dd className="text-lg font-semibold text-slate-100">{item.value}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Callback URL Info */}
      <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/10 p-4">
        <h3 className="text-sm font-medium text-indigo-200 mb-2">Callback Endpoint</h3>
        <div className="flex items-center justify-between">
          <code className="text-sm text-indigo-300">
            {API_BASE_URL}/callback/receive
          </code>
          <button
            onClick={() => copyToClipboard(`${API_BASE_URL}/callback/receive`)}
            className="ml-4 text-indigo-300 hover:text-indigo-200"
          >
            Copy
          </button>
        </div>
        <p className="mt-2 text-xs text-indigo-300/80">
          Use this URL as your callback endpoint when creating transactions
        </p>
      </div>

      {/* Filter */}
      <div className="rounded-xl border border-slate-700 bg-slate-800 shadow-sm p-4">
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={excludeLocalhost}
              onChange={(e) => {
                setExcludeLocalhost(e.target.checked);
                setPage(1);
              }}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-600 bg-slate-700 rounded"
            />
            <span className="ml-2 text-sm text-slate-300">Hide Localhost</span>
          </label>
        </div>
      </div>

      {/* Callbacks Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-700 bg-slate-800 shadow-sm overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-slate-700">
            <h3 className="text-lg leading-6 font-medium text-slate-100">
              Recent Callbacks ({totalItems})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Transaction Index</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Source IP</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Received At</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {callbacks.map((callback) => (
                  <tr key={callback.id} className="hover:bg-slate-700/40 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-200">#{callback.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-200">
                      {callback.transactionIndex || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(callback.status)}`}>
                        <span className="mr-1">{getStatusIcon(callback.status)}</span>
                        {callback.status || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                      {callback.sourceIp || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                      {formatDate(callback.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => setSelectedCallback(callback)}
                        className="text-indigo-400 hover:text-indigo-300"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {callbacks.length === 0 && (
              <div className="text-center py-12">
                <ClipboardDocumentCheckIcon className="mx-auto h-12 w-12 text-slate-500" />
                <h3 className="mt-2 text-sm font-medium text-slate-200">No callbacks</h3>
                <p className="mt-1 text-sm text-slate-400">
                  Callbacks will appear here when transactions complete.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {/* Detail Modal */}
      {selectedCallback && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/60 transition-opacity" onClick={() => setSelectedCallback(null)}></div>
            <div className="inline-block align-bottom bg-slate-800 border border-slate-700 rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-slate-100 mb-4">
                  Callback Details #{selectedCallback.id}
                </h3>
                <div className="space-y-3">
                  <div>
                    <strong className="text-sm text-slate-400">Transaction Index:</strong>
                    <p className="text-sm text-slate-200">{selectedCallback.transactionIndex || '-'}</p>
                  </div>
                  <div>
                    <strong className="text-sm text-slate-400">Signature:</strong>
                    <p className="text-sm text-slate-200 font-mono break-all">{selectedCallback.signature || '-'}</p>
                  </div>
                  <div>
                    <strong className="text-sm text-slate-400">Status:</strong>
                    <p className="text-sm text-slate-200">{selectedCallback.status || '-'}</p>
                  </div>
                  <div>
                    <strong className="text-sm text-slate-400">Source IP:</strong>
                    <p className="text-sm text-slate-200">{selectedCallback.sourceIp || '-'}</p>
                  </div>
                  <div>
                    <strong className="text-sm text-slate-400">Received At:</strong>
                    <p className="text-sm text-slate-200">{formatDate(selectedCallback.createdAt)}</p>
                  </div>
                  <div>
                    <strong className="text-sm text-slate-400">Full Payload:</strong>
                    <pre className="mt-2 text-xs bg-slate-900 text-slate-200 p-4 rounded-lg overflow-x-auto">
                      {JSON.stringify(selectedCallback.payload, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6">
                <button
                  type="button"
                  className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500 sm:text-sm"
                  onClick={() => setSelectedCallback(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallbackLogs;
